import {
  Button,
  TagBadge,
  MenuTrigger,
  Menu,
  MenuItem,
  MenuSeparator,
  ListCardRow,
} from '@chatsdet/ui';
import { DotsThreeVertical, Globe, PencilSimple, Trash, Star } from '@phosphor-icons/react';
import { useSandboxEnvironment } from '../../../hooks/useSandboxEnvironment';
import {
  getEnvironmentRowPresentation,
  SANDBOX_STATUS_BADGE,
  type EnvironmentItem,
} from './propertyEnvironmentPresentation';

type PropertyEnvironmentRowProps = {
  propertyId: string;
  environment: EnvironmentItem;
  index: number;
  isOnlyEnvironment: boolean;
  onEdit: (environment: EnvironmentItem) => void;
  onDelete: (environmentId: string) => void;
  onSetDefault: (environmentId: string) => void;
};

/**
 * Live readiness badge for a sandbox environment row.
 *
 * A v1 (template-backed) row polls the shared-instance status route. A v2
 * row has no such persistent instance to poll — every run claims its own
 * disposable copy, torn down after — so it renders a static badge instead;
 * polling the v1 route for it would 400 (`ENV_NOT_SANDBOX`).
 */
function SandboxEnvStatusBadge({
  propertyId,
  environmentId,
  isV2,
}: {
  propertyId: string;
  environmentId: string;
  isV2: boolean;
}) {
  const { sandbox } = useSandboxEnvironment(propertyId, environmentId, { enabled: !isV2 });
  if (isV2) return <TagBadge name="Sandboxes v2" color="purple" size="sm" />;
  if (!sandbox) return null;
  const cfg = SANDBOX_STATUS_BADGE[sandbox.status] ?? SANDBOX_STATUS_BADGE.asleep!;
  return <TagBadge name={cfg.label} color={cfg.color} size="sm" />;
}

export function PropertyEnvironmentRow({
  propertyId,
  environment,
  index,
  isOnlyEnvironment,
  onEdit,
  onDelete,
  onSetDefault,
}: PropertyEnvironmentRowProps) {
  const isLocal = environment.type === 'local';
  const presentation = getEnvironmentRowPresentation(environment);

  return (
    <ListCardRow
      index={index}
      icon={<Globe size={16} />}
      title={environment.name}
      badges={
        <>
          {environment.isDefault && <TagBadge name="Default" color="green" size="sm" />}
          {environment.environmentKindName && (
            <TagBadge name={`Stage: ${environment.environmentKindName}`} color="blue" size="sm" />
          )}
          <TagBadge
            name={presentation.typeBadge.label}
            color={presentation.typeBadge.color}
            size="sm"
          />
          {environment.type === 'sandbox' && (
            <SandboxEnvStatusBadge
              propertyId={propertyId}
              environmentId={environment.id}
              isV2={!!environment.sandboxV2Environment}
            />
          )}
          {environment.customerAwsRoleArn && <TagBadge name="VPC" color="blue" size="sm" />}
        </>
      }
      meta={
        <>
          {presentation.instanceDescriptor && (
            <>
              <span className="whitespace-nowrap">{presentation.instanceDescriptor}</span>
              <span aria-hidden>·</span>
            </>
          )}
          <span className="truncate">{environment.url}</span>
          <span aria-hidden>·</span>
          <span className="whitespace-nowrap">Updated {presentation.updatedAtLabel}</span>
          {presentation.expiryLabel && (
            <>
              <span aria-hidden>·</span>
              <span className="whitespace-nowrap">{presentation.expiryLabel}</span>
            </>
          )}
        </>
      }
      actions={
        <MenuTrigger>
          <Button variant="ghost" size="sm" aria-label={`Actions for ${environment.name}`}>
            <DotsThreeVertical size={16} weight="bold" />
          </Button>
          <Menu
            onAction={(key) => {
              if (key === 'set-default') onSetDefault(environment.id);
              if (key === 'edit') onEdit(environment);
              if (key === 'delete') onDelete(environment.id);
            }}
          >
            {!environment.isDefault && !isLocal ? (
              <MenuItem id="set-default">
                <Star size={14} className="mr-2" />
                Set as default
              </MenuItem>
            ) : null}
            {!isLocal ? (
              <MenuItem id="edit">
                <PencilSimple size={14} className="mr-2" />
                Edit
              </MenuItem>
            ) : null}
            {!isLocal || !environment.isDefault ? <MenuSeparator /> : null}
            <MenuItem id="delete" destructive isDisabled={isOnlyEnvironment}>
              <Trash size={14} className="mr-2" />
              Delete
            </MenuItem>
          </Menu>
        </MenuTrigger>
      }
    />
  );
}
